import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';

import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import '../../styles/design-tokens.css';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;

const navItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: DashboardIcon,
    path: '/',
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: ShoppingCartIcon,
    path: '/orders',
    children: [
      { label: 'Part Orders', path: '/orders/parts' },
      { label: 'Supplier Quotes', path: '/orders/quotes' },
      { label: 'Part Replenishment', path: '/orders/part-replenishment' },
    ],
  },
  {
    id: 'shipments',
    label: 'Shipments',
    icon: LocalShippingIcon,
    path: '/shipments',
    children: [
      { label: 'Dispatch Board', path: '/shipments/dispatch' },
      { label: 'Replenishment', path: '/shipments/replenishment-shipments' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: InventoryIcon,
    path: '/inventory',
    children: [
      { label: 'Parts & Locations', path: '/inventory/search'          },
      { label: 'Suppliers',         path: '/inventory/suppliers'        },
      { label: 'Reverse Logistics', path: '/inventory/reverse-logistics' },
      { label: 'Smart Stocking',    path: '/inventory/smart-stocking'   },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: AccountBalanceIcon,
    path: '/accounting',
    children: [
      { label: 'Purchase Orders',   path: '/accounting/purchase-orders'   },
      { label: 'Payments',          path: '/accounting/payments'          },
      { label: 'Reports',           path: '/accounting/reports'           },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon,
    path: '/settings',
    children: [
      { label: 'Customers',     path: '/settings/customers'     },
      { label: 'Users',         path: '/settings/users'         },
      { label: 'Notifications', path: '/settings/notifications' },
      { label: 'Integrations',  path: '/settings/integrations'  },
    ],
  },
];

// Determine which parent item is active based on current path
function getActiveParent(pathname) {
  for (const item of navItems) {
    if (item.children) {
      if (pathname === item.path || item.children.some((c) => pathname === c.path || pathname.startsWith(c.path + '/'))) {
        return item.id;
      }
    }
  }
  return null;
}

export default function Sidebar({ collapsed }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef(null);

  // Compact-mode popout: { id, top }
  const [popoutItem, setPopoutItem] = useState(null);
  const closeTimerRef = useRef(null);

  const handleScroll = () => {
    setIsScrolling(true);
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 800);
  };

  useEffect(() => () => {
    clearTimeout(scrollTimerRef.current);
    clearTimeout(closeTimerRef.current);
  }, []);

  // Close popout when sidebar expands
  useEffect(() => {
    if (!collapsed) setPopoutItem(null);
  }, [collapsed]);

  const [openSections, setOpenSections] = useState(() => {
    const active = getActiveParent(location.pathname);
    return active ? { [active]: true } : {};
  });

  // Auto-expand the active section when route changes
  useEffect(() => {
    const active = getActiveParent(location.pathname);
    if (active) {
      setOpenSections((prev) => ({ ...prev, [active]: true }));
    }
  }, [location.pathname]);

  const handleParentClick = (item) => {
    if (item.children) {
      if (!collapsed) {
        // Expanded mode: toggle submenu only, no navigation
        setOpenSections((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
      } else {
        // Compact mode: navigate immediately to first child
        navigate(item.children[0].path);
        setPopoutItem(null);
      }
    } else {
      navigate(item.path);
    }
  };

  // Chevron-only toggle: expand/collapse without navigating
  const handleChevronClick = (e, item) => {
    e.stopPropagation();
    setOpenSections((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  };

  // Popout hover handlers
  const handleIconMouseEnter = (item, e) => {
    if (!collapsed || !item.children) return;
    clearTimeout(closeTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoutItem({ id: item.id, top: rect.top });
  };

  const handleIconMouseLeave = () => {
    if (!collapsed) return;
    closeTimerRef.current = setTimeout(() => setPopoutItem(null), 150);
  };

  const handlePopoutMouseEnter = () => {
    clearTimeout(closeTimerRef.current);
  };

  const handlePopoutMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setPopoutItem(null), 150);
  };

  const handlePopoutChildClick = (path) => {
    navigate(path);
    setPopoutItem(null);
  };

  const isItemActive = (path) => {
    if (path.includes('?')) {
      const [pathPart, queryPart] = path.split('?');
      if (location.pathname !== pathPart) return false;
      const params = new URLSearchParams(queryPart);
      const currentParams = new URLSearchParams(location.search);
      for (const [key, value] of params.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    }
    if (location.pathname === path || location.pathname.startsWith(path + '/')) return true;
    // Parts & Locations covers stock-location detail pages
    if (path === '/inventory/search' && location.pathname.startsWith('/inventory/stock-locations')) return true;
    return false;
  };
  const isParentActive = (item) =>
    item.path === location.pathname ||
    (item.children && item.children.some((c) => isItemActive(c.path)));

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  // Find the popout nav item
  const popoutNavItem = popoutItem ? navItems.find((n) => n.id === popoutItem.id) : null;

  return (
    <>
      <Box
        component="nav"
        sx={{ position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: sidebarWidth,
          backgroundColor: 'var(--paper)',
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
          transition: 'width 0.2s ease',
          overflow: 'hidden',
        }}
      >
        {/* 80px spacer — aligns nav below the fixed TopBar */}
        <Box sx={{ height: 80, flexShrink: 0 }} />

        {/* Navigation */}
        <Box
          onScroll={handleScroll}
          sx={{ flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            py: 1,
            px: '5px',
            // Thin custom scrollbar
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              background: isScrolling ? '#b0bec5' : 'transparent',
              transition: 'background 0.4s ease',
            },
            // Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: isScrolling ? '#b0bec5 transparent' : 'transparent transparent',
          }}
        >
          <List dense disablePadding>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isParentActive(item);
              const isOpen = openSections[item.id];
              const hasChildren = Boolean(item.children);

              return (
                <React.Fragment key={item.id}>
                  {/* Wrap with MUI Tooltip only for leaf items in compact mode */}
                  <Tooltip
                    title={collapsed && !hasChildren ? item.label : ''}
                    placement="right"
                    disableHoverListener={!collapsed || hasChildren}
                  >
                    <ListItemButton
                      onClick={() => handleParentClick(item)}
                      onMouseEnter={(e) => handleIconMouseEnter(item, e)}
                      onMouseLeave={handleIconMouseLeave}
                      selected={active && !hasChildren}
                      sx={{ // Collapsed: strict 46×46 box, icon centred
                        ...(collapsed ? {
                          width: 46,
                          height: 46,
                          minHeight: 46,
                          p: 0,
                          mx: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        } : {
                          minHeight: 35,
                          pt: '8px',
                          pb: '8px',
                          pl: '24px',
                          pr: '16px',
                          mx: 1,
                        }),
                        borderRadius: '6px',
                        mb: '4px',
                        backgroundColor: active ? 'var(--secondary-light)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'var(--secondary-light)',
                          '& .nav-label':        { color: 'var(--secondary-main)' },
                          '& .nav-chevron':      { color: 'var(--secondary-main)' },
                          '& .compact-chevron':  { color: 'var(--secondary-main)' },
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'var(--secondary-light)',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'var(--secondary-light)',
                        },
                      }}
                    >
                      <ListItemIcon
                        className="nav-icon"
                        sx={{ minWidth: collapsed ? 0 : 32,
                          color: active ? 'var(--secondary-main)' : '#364152',
                          mr: collapsed ? 0 : '4px',
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                      </ListItemIcon>

                      {/* Compact-mode chevron indicator for items with children */}
                      {collapsed && hasChildren && (
                        <Box
                          className="compact-chevron"
                          sx={{ position: 'absolute',
                            right: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            lineHeight: 1,
                            color: active ? 'var(--secondary-main)' : '#9aa5b4',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <ChevronRightIcon sx={{ fontSize: 11 }} />
                        </Box>
                      )}

                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              color: active ? 'var(--secondary-main)' : '#364152',
                              className: 'nav-label',
                              noWrap: true,
                            }}
                          />
                          {hasChildren && (
                            <Box
                              className="nav-chevron"
                              onClick={(e) => handleChevronClick(e, item)}
                              sx={{ color: active ? 'var(--secondary-main)' : 'inherit' }}
                            >
                              {isOpen ? (
                                <ExpandLessIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 16 }} />
                              )}
                            </Box>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>

                  {/* Sub-items (expanded mode only) */}
                  {hasChildren && !collapsed && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {/* Tree-branch container — vertical line at 28px from sidebar left */}
                      <Box
                        sx={{ ml: '40px',
                          mr: '8px',
                          borderLeft: '1px solid #e3e8ef',
                        }}
                      >
                        {item.children.map((child) => {
                          const childActive = isItemActive(child.path);
                          return (
                            <ListItemButton
                              key={child.path}
                              onClick={() => navigate(child.path)}
                              sx={{ minHeight: 40,
                                pl: '14px',
                                pr: 1,
                                py: '8px',
                                borderRadius: '0 6px 6px 0',
                                mb: '1px',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  '& .sub-dot': { backgroundColor: '#009688' },
                                  '& .sub-label': { color: '#009688' },
                                },
                              }}
                            >
                              {/* Bullet dot */}
                              <Box
                                className="sub-dot"
                                sx={{ width: 4,
                                  height: 4,
                                  borderRadius: '50%',
                                  backgroundColor: childActive ? '#009688' : '#697586',
                                  mr: '10px',
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontSize: '14px',
                                  fontWeight: childActive ? 500 : 400,
                                  color: childActive ? '#009688' : '#364152',
                                  className: 'sub-label',
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* Compact-mode popout submenu — rendered outside the sidebar so it's not clipped */}
      {collapsed && popoutNavItem && (
        <Paper
          elevation={4}
          onMouseEnter={handlePopoutMouseEnter}
          onMouseLeave={handlePopoutMouseLeave}
          sx={{ position: 'fixed',
            left: SIDEBAR_COLLAPSED + 6,
            top: popoutItem.top,
            zIndex: 1300,
            minWidth: 180,
            py: '8px',
            px: 0,
            borderRadius: '8px',
            backgroundColor: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          }}
        >
          <List dense disablePadding>
            {popoutNavItem.children.map((child) => {
              const childActive = isItemActive(child.path);
              return (
                <ListItemButton
                  key={child.path}
                  onClick={() => handlePopoutChildClick(child.path)}
                  sx={{ px: '16px',
                    py: '8px',
                    minHeight: 36,
                    gap: '10px',
                    '&:hover': {
                      backgroundColor: 'var(--secondary-light)',
                      '& .popout-dot': { backgroundColor: '#009688' },
                      '& .popout-label': { color: '#009688' },
                    },
                  }}
                >
                  {/* Bullet dot */}
                  <Box
                    className="popout-dot"
                    sx={{ width: 5,
                      height: 5,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: childActive ? '#009688' : '#9aa5b4',
                    }}
                  />
                  <Typography
                    className="popout-label"
                    sx={{ fontSize: '0.875rem',
                      fontWeight: childActive ? 500 : 400,
                      color: childActive ? '#009688' : '#364152',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {child.label}
                  </Typography>
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
    </>
  );
}
